import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface HandicapConfig {
  base_average: number;
  handicap_percentage: number;
  calculation_frequency: string;
  lock_handicap_after_weeks: number | null;
  lock_starting_hdcp_for_weeks?: number | null;
  a_max: number | null;
  b_max: number | null;
  c_max: number | null;
  d_max: number | null;
  e_max: number | null;
  league_max: number | null;
  handicap_type: 'individual' | 'team_difference';
  team_difference_percentage: number | null;
}

interface Props {
  leagueId: number;
  initialConfig: HandicapConfig;
  onSaved: (config: HandicapConfig) => void;
  onClose: () => void;
}

const AdminHandicapConfigModal: React.FC<Props> = ({ leagueId, initialConfig, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<HandicapConfig>({ ...initialConfig });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setConfig({ ...initialConfig });
  }, [initialConfig]);

  const set = (field: keyof HandicapConfig, value: any) =>
    setConfig(prev => ({ ...prev, [field]: value }));

  const numOrNull = (v: string) => v === '' ? null : Number(v);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/handicap-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        onSaved(config);
        onClose();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to save configuration');
      }
    } catch (e) {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 rounded-xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-blue-900/90 backdrop-blur-sm border-b border-white/20 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            {t('admin.managingLeague.editHandicapConfig')}
          </h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Handicap Type */}
          <div>
            <label className="block text-sm font-medium text-blue-200 mb-2">
              {t('admin.managingLeague.handicapType')}
            </label>
            <select
              value={config.handicap_type}
              onChange={e => set('handicap_type', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="individual" className="bg-gray-800">{t('admin.managingLeague.handicapTypeIndividual')}</option>
              <option value="team_difference" className="bg-gray-800">{t('admin.managingLeague.handicapTypeTeamDiff')}</option>
            </select>
            <p className="text-blue-300 text-xs mt-1">
              {config.handicap_type === 'team_difference'
                ? t('admin.managingLeague.handicapTypeTeamDiffHelp')
                : t('admin.managingLeague.handicapTypeIndividualHelp')}
            </p>
          </div>

          {/* Team Difference Percentage (shown only for team_difference type) */}
          {config.handicap_type === 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">
                {t('admin.managingLeague.teamDiffPercentage')} *
              </label>
              <input
                type="number"
                min={1} max={100} step={1}
                value={config.team_difference_percentage ?? ''}
                onChange={e => set('team_difference_percentage', numOrNull(e.target.value))}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="80"
              />
              <p className="text-blue-300 text-xs mt-1">{t('admin.managingLeague.teamDiffPercentageHelp')}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Base Average - only for individual leagues */}
            {config.handicap_type !== 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">{t('admin.managingLeague.baseAverage')} *</label>
              <input
                type="number" min={100} max={300}
                value={config.base_average}
                onChange={e => set('base_average', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            )}
            {/* Handicap Percentage - only for individual leagues */}
            {config.handicap_type !== 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">{t('admin.managingLeague.handicapPercent')} *</label>
              <input
                type="number" min={1} max={100} step={1}
                value={config.handicap_percentage}
                onChange={e => set('handicap_percentage', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            )}
            {/* Calculation Frequency - only for individual leagues */}
            {config.handicap_type !== 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">{t('admin.managingLeague.frequency')}</label>
              <select
                value={config.calculation_frequency}
                onChange={e => set('calculation_frequency', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="per_league" className="bg-gray-800">Per League</option>
                <option value="weekly" className="bg-gray-800">Weekly</option>
                <option value="monthly" className="bg-gray-800">Monthly</option>
                <option value="yearly" className="bg-gray-800">Yearly</option>
              </select>
            </div>
            )}
            {/* Lock Handicap After Weeks - only for individual leagues */}
            {config.handicap_type !== 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">{t('admin.managingLeague.lockAfter')}</label>
              <input
                type="number" min={1} max={52}
                value={config.lock_handicap_after_weeks ?? ''}
                onChange={e => set('lock_handicap_after_weeks', numOrNull(e.target.value))}
                placeholder="Optional"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            )}
            {/* League Max - only for individual leagues */}
            {config.handicap_type !== 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">{t('admin.managingLeague.leagueMax')}</label>
              <input
                type="number" min={0} max={200}
                value={config.league_max ?? ''}
                onChange={e => set('league_max', numOrNull(e.target.value))}
                placeholder="No limit"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            )}
          </div>

          {/* Division Max Caps - only for individual leagues */}
          {config.handicap_type !== 'team_difference' && (
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">🎯 {t('admin.managingLeague.maximumHandicaps')}</h4>
            <div className="grid grid-cols-3 gap-3">
              {(['a', 'b', 'c', 'd', 'e'] as const).map(div => (
                <div key={div}>
                  <label className="block text-xs font-medium text-blue-300 mb-1">Div {div.toUpperCase()} Max</label>
                  <input
                    type="number" min={0} max={200}
                    value={(config[`${div}_max` as keyof HandicapConfig] as number | null) ?? ''}
                    onChange={e => set(`${div}_max` as keyof HandicapConfig, numOrNull(e.target.value))}
                    placeholder="—"
                    className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded text-white text-sm placeholder-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3 text-red-300 text-sm">{error}</div>
          )}
        </div>

        <div className="sticky bottom-0 bg-blue-900/90 backdrop-blur-sm border-t border-white/20 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-2 rounded-lg font-bold transition"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHandicapConfigModal;
